// (A) 중립 Menu API — antd 미접촉. (B) PlainMenu(커스텀 + Radix Popover flyout)에 위임.
// standalone 네비/드롭다운 콘텐츠 Menu.
// OmwMenuItem·OmwMenuClickInfo 타입은 뷰어가 소비(OmwDropdownMenu 와 공유).
import { forwardRef } from 'react'
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { PlainMenu } from '../_impl/PlainMenu'

/** 클릭 콜백에 전달되는 정보(antd MenuInfo 의 중립화 — key + 트리거 DOM 이벤트). */
export interface OmwMenuClickInfo {
  key: string
  /** 트리거된 DOM 이벤트(stopPropagation 등 필요 시). */
  domEvent?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
}

/**
 * 중립 메뉴 항목. antd `MenuProps['items']`(ItemType) 를 정규화한다.
 * 실측 필드: key/label/onClick/type/title/className/children/disabled/style/icon.
 */
export interface OmwMenuItem {
  /** 일반 항목은 필수. divider/group 은 antd 처럼 생략 가능. */
  key?: string
  label?: ReactNode
  icon?: ReactNode
  disabled?: boolean
  danger?: boolean
  title?: string
  /**
   * 항목의 명시적 접근명(aria-label). 아이콘 전용(라벨 텍스트가 시각적으로 숨겨진) 항목에서
   * 스크린리더 이름을 지정한다. 설정 시 항목 요소에 aria-label 이 부여되고, 장식용 아이콘은
   * aria-hidden 처리되어 접근명 계산에서 제외된다(예: 인라인 SVG 에 내장된 파일명 `<title>` 오염 차단).
   */
  ariaLabel?: string
  /** 'group'=그룹 헤더, 'divider'=구분선. 미지정 시 일반 항목/서브메뉴(children 존재 시). */
  type?: 'group' | 'divider'
  children?: OmwMenuItem[]
  onClick?: (info: OmwMenuClickInfo) => void
  /** 항목 hover 콜백. */
  onMouseEnter?: (info: OmwMenuClickInfo) => void
  onMouseLeave?: (info: OmwMenuClickInfo) => void
  /** 서브메뉴 팝업 className. */
  popupClassName?: string
  /** 서브메뉴 팝업 위치 offset. */
  popupOffset?: [number, number]
  className?: string
  style?: CSSProperties
  /**
   * data-* 패스스루 속성. 렌더된 항목 DOM 에 그대로 forward 된다(antd DataAttributes 호환).
   */
  [dataAttr: `data-${string}`]: unknown
}

/** 명령형 핸들(실측: MailToolbar overflow 측정). antd 내부 ref 를 중립 표면으로 노출. */
export interface OmwMenuRef {
  /**
   * horizontal 모드에서 넘친 항목이 접힌 "더보기" 항목의 DOM 엘리먼트.
   * 실측: MailToolbar 가 위치(getBoundingClientRect) 측정 후 팝업 최대 높이 CSS 변수 계산.
   * overflow 미발생 시 null.
   */
  getOverflowRestElement: () => HTMLElement | null
}

export interface OmwMenuProps {
  items: OmwMenuItem[]
  /** 실측: 'inline'(MenuLeft)·'horizontal'(MenuTop). */
  mode?: 'inline' | 'horizontal' | 'vertical'
  selectedKeys?: string[]
  openKeys?: string[]
  onOpenChange?: (keys: string[]) => void
  onClick?: (info: OmwMenuClickInfo) => void
  selectable?: boolean
  inlineIndent?: number
  /** 접힌 overflow "더보기" 팝업 className(실측: MailToolbar). */
  overflowedIndicatorPopupClassName?: string
  /** 서브메뉴/overflow 트리거 동작(실측: MailToolbar overflow 'click'). */
  triggerSubMenuAction?: 'click' | 'hover'
  className?: string
  style?: CSSProperties
}

export const OmwMenu = forwardRef<OmwMenuRef, OmwMenuProps>(function OmwMenu(props, ref) {
  return <PlainMenu {...props} menuRef={ref} />
})
